import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { TableService } from '../shared/services/table.service';
import { OrderService } from '../shared/services/order.service';
import { BillingService } from '../shared/services/billing.service';
import { AuthService, User } from '../shared/services/auth.service';
import { DataInitService } from '../shared/services/data-init.service';
import { ConfiguracionService } from '../shared/services/configuracion.service';

const ROLE_ROUTES: Record<User['role'], string[]> = {
  admin: ['/dashboard', '/floor-plan', '/menu', '/orders', '/kitchen', '/billing', '/reports', '/insumos'],
  mesero: ['/dashboard', '/floor-plan', '/menu', '/orders'],
  cocinero: ['/dashboard', '/kitchen'],
  cajero: ['/dashboard', '/billing', '/reports', '/insumos'],
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  stats = {
    totalTables: 0,
    occupiedTables: 0,
    availableTables: 0,
    activeOrders: 0,
    pendingOrders: 0,
    todayRevenue: 0,
    todayBills: 0,
    averageTicket: 0
  };

  recentOrders: any[] = [];
  topProducts: any[] = [];
  lastUpdated: Date | null = null;

  /** Datos para gráficas */
  ordersByStatus: { status: string; label: string; count: number; color: string; startPercent: number; endPercent: number }[] = [];
  maxProductCount = 0;
  revenueByDay: { date: Date; label: string; total: number }[] = [];
  maxRevenueDay = 0;

  private refreshSubscription?: Subscription;
  private readonly STATUS_COLORS: { [key: string]: string } = {
    CONFIRMED: '#3498db',
    IN_KITCHEN: '#f39c12',
    READY_TO_SERVE: '#27ae60',
    COMPLETED: '#2ecc71',
    INVOICED: '#1e8449',
    DRAFT: '#95a5a6',
    CANCELLED: '#e74c3c'
  };

  constructor(
    private tableService: TableService,
    private orderService: OrderService,
    private billingService: BillingService,
    private authService: AuthService,
    private dataInitService: DataInitService,
    private configuracionService: ConfiguracionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Inicializar datos de prueba si no existen
    this.dataInitService.initializeTestData();

    // Cargar datos iniciales
    this.loadDashboardData();

    // Actualizar cada 5 segundos
    this.refreshSubscription = interval(5000).subscribe(() => {
      this.loadDashboardData();
    });
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  loadDashboardData(): void {
    this.lastUpdated = new Date();
    // Estadísticas de mesas
    const tables = this.tableService.getTables();
    this.stats.totalTables = tables.length;
    this.stats.occupiedTables = tables.filter(t => t.status === 'OCCUPIED').length;
    this.stats.availableTables = tables.filter(t => t.status === 'AVAILABLE').length;

    // Estadísticas de órdenes
    const orders = this.orderService.getOrders();
    this.stats.activeOrders = orders.filter(o => 
      o.status === 'IN_KITCHEN' || o.status === 'READY_TO_SERVE' || o.status === 'CONFIRMED'
    ).length;
    this.stats.pendingOrders = orders.filter(o => o.status === 'DRAFT').length;

    // Estadísticas de facturación (hoy)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bills = this.billingService.getBills();
    const todayBills = bills.filter(b => {
      const billDate = new Date(b.createdAt);
      billDate.setHours(0, 0, 0, 0);
      return billDate.getTime() === today.getTime();
    });

    this.stats.todayBills = todayBills.length;
    this.stats.todayRevenue = todayBills.reduce((sum, bill) => sum + bill.total, 0);
    this.stats.averageTicket = todayBills.length > 0 
      ? this.stats.todayRevenue / todayBills.length 
      : 0;

    // Órdenes recientes
    this.recentOrders = orders
      .filter(o => o.status !== 'COMPLETED' && o.status !== 'CANCELLED')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    // Productos más vendidos
    const menuItems = this.orderService.getMenuItems();
    const productCounts: { [key: string]: { name: string; count: number; revenue: number } } = {};
    
    orders.forEach(order => {
      order.items.forEach(item => {
        const menuItem = menuItems.find(mi => mi.id === item.menuItemId);
        const itemName = menuItem ? menuItem.name : `Item ${item.menuItemId}`;
        
        if (!productCounts[item.menuItemId]) {
          productCounts[item.menuItemId] = {
            name: itemName,
            count: 0,
            revenue: 0
          };
        }
        productCounts[item.menuItemId].count += item.quantity;
        productCounts[item.menuItemId].revenue += item.subtotal;
      });
    });

    this.topProducts = Object.values(productCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
    this.maxProductCount = Math.max(1, ...this.topProducts.map(p => p.count));

    // Pedidos por estado (para gráfica donut)
    const statusLabels: { [key: string]: string } = {
      DRAFT: 'Borrador',
      CONFIRMED: 'Confirmada',
      IN_KITCHEN: 'En cocina',
      READY_TO_SERVE: 'Lista',
      COMPLETED: 'Completada',
      INVOICED: 'Facturado',
      CANCELLED: 'Cancelada'
    };
    const statusCounts: { [key: string]: number } = {};
    orders.forEach(o => {
      statusCounts[o.status] = (statusCounts[o.status] || 0) + 1;
    });
    const totalOrders = orders.length;
    const statusEntries = Object.entries(statusCounts)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => ({
        status,
        label: statusLabels[status] || status,
        count,
        color: this.STATUS_COLORS[status] || '#95a5a6'
      }))
      .sort((a, b) => b.count - a.count);
    let acc = 0;
    this.ordersByStatus = statusEntries.map(seg => {
      const pct = totalOrders > 0 ? (seg.count / totalOrders) * 100 : 0;
      const startPercent = acc;
      acc += pct;
      return { ...seg, startPercent, endPercent: acc };
    });

    // Ingresos últimos 7 días (para gráfica de barras)
    const last7: { date: Date; total: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const dayBills = bills.filter(b => {
        const bd = new Date(b.createdAt);
        bd.setHours(0, 0, 0, 0);
        return bd.getTime() === d.getTime();
      });
      const total = dayBills.reduce((s, b) => s + b.total, 0);
      last7.push({ date: d, total });
    }
    this.revenueByDay = last7.map(({ date, total }) => ({
      date,
      label: date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }),
      total
    }));
    this.maxRevenueDay = Math.max(1, ...this.revenueByDay.map(d => d.total));
  }

  getCurrentUser() {
    return this.authService.getCurrentUser();
  }

  canShowRoute(path: string): boolean {
    const user = this.authService.getCurrentUser();
    if (!user) return false;
    return ROLE_ROUTES[user.role]?.includes(path) ?? false;
  }

  /** Indica si el usuario actual tiene el permiso (según rol en ConfiguracionService). */
  hasPermiso(permisoClave: string): boolean {
    const user = this.authService.getCurrentUser();
    if (!user) return false;
    return this.configuracionService.hasPermiso(user.role, permisoClave);
  }

  getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 19) return 'Buenas tardes';
    return 'Buenas noches';
  }

  getCurrentDateFormatted(): string {
    return new Date().toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  getRoleLabel(role: string): string {
    const roles: { [key: string]: string } = {
      'admin': 'Administrador',
      'mesero': 'Mesero',
      'cocinero': 'Cocinero',
      'cajero': 'Cajero'
    };
    return roles[role] || role;
  }

  formatDate(date: string | Date): string {
    const d = new Date(date);
    return d.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getOrderStatusClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'DRAFT': 'draft',
      'CONFIRMED': 'confirmed',
      'IN_KITCHEN': 'preparing',
      'READY_TO_SERVE': 'ready',
      'COMPLETED': 'completed',
      'INVOICED': 'invoiced',
      'CANCELLED': 'cancelled'
    };
    return statusMap[status] || 'draft';
  }

  getOrderStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      'DRAFT': 'Borrador',
      'CONFIRMED': 'Confirmada',
      'IN_KITCHEN': 'En Cocina',
      'READY_TO_SERVE': 'Lista',
      'COMPLETED': 'Completada',
      'INVOICED': 'Facturado',
      'CANCELLED': 'Cancelada'
    };
    return statusMap[status] || status;
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }

  /** Genera el conic-gradient para la donut de pedidos por estado. */
  getDonutConic(segments: { startPercent: number; endPercent: number; color: string }[]): string {
    if (!segments || segments.length === 0) return 'transparent';
    const parts = segments.map(s => `${s.color} ${s.startPercent}% ${s.endPercent}%`);
    return `conic-gradient(${parts.join(', ')})`;
  }

  /** Genera el conic-gradient para ocupación de mesas (disponibles vs ocupadas). */
  getTablesConic(): string {
    const total = this.stats.totalTables || 1;
    const pctAvailable = (this.stats.availableTables / total) * 100;
    const pctOccupied = (this.stats.occupiedTables / total) * 100;
    if (pctOccupied <= 0) return 'conic-gradient(var(--success-color) 0% 100%)';
    if (pctAvailable <= 0) return 'conic-gradient(var(--info-color) 0% 100%)';
    return `conic-gradient(var(--success-color) 0% ${pctAvailable}%, var(--info-color) ${pctAvailable}% 100%)`;
  }
}
